import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge, ProgressBar, Spinner } from 'react-bootstrap';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { JobEstimateProvider, JobEstimate } from '../../features/machine-intervals/EstimateProvider.js';
import SearchBox from '../common/SearchBox';
import ProductColorDot from '../common/ProductColorDot';
import { useAdvanceTableContext } from '../../providers/AdvanceTableProvider';
import { getJobStatusUI } from '../../helpers/jobStatus';
import {
  PHASES_ORDER,
  PHASE_LABEL
} from '../../pages/production-management/ProjectJobsMonitor';

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

function toPercent(p) {
  const x = Number(p ?? 0);
  if (!Number.isFinite(x)) return 0;
  return x <= 1
    ? Math.round(clamp01(x) * 100)
    : Math.round(Math.max(0, Math.min(100, x)));
}

function phaseColor(phaseKey) {
  const map = {
    unwinding: 'info',
    pre_stranding: 'primary',
    conductor_isolation: 'primary',
    rope: 'warning',
    interior_sheat: 'info',
    exterior_sheat: 'success',
    rewinding: 'secondary',
    coiling: 'dark'
  };
  return map[phaseKey] || 'primary';
}

const PhaseProgressBar = ({ job }) => {
  const order = job?.phasesForGant?.length ? job.phasesForGant : PHASES_ORDER;
  const phases = job?.phases || {};

  const rows = useMemo(() => {
    return order.map(phaseKey => {
      const phase = phases?.[phaseKey];
      const exists = !!phase;
      const percent = exists ? toPercent(phase.progress) : 0;
      return {
        phaseKey,
        label: PHASE_LABEL[phaseKey] || phaseKey,
        exists,
        percent
      };
    });
  }, [order, phases]);

  return (
    <div style={{ minWidth: 260 }}>
      <div className="d-flex gap-1 align-items-center">
        {rows.map(r => (
          <div
            key={r.phaseKey}
            title={`${r.label}: ${r.exists ? r.percent : 0}%`}
            style={{
              flex: 1,
              opacity: r.exists ? 1 : 0.25,
              filter: r.exists ? 'none' : 'grayscale(1)'
            }}
          >
            <ProgressBar
              now={r.percent}
              variant={phaseColor(r.phaseKey)}
              style={{
                height: 10,
                borderRadius: 999,
                background: 'rgba(148, 163, 184, 0.25)'
              }}
            />
          </div>
        ))}
      </div>
      <div className="d-flex gap-1 mt-1">
        {rows.map(r => (
          <div
            key={r.phaseKey + '_lbl'}
            style={{
              flex: 1,
              fontSize: 10,
              textAlign: 'center',
              opacity: r.exists ? 0.9 : 0.25,
              whiteSpace: 'nowrap'
            }}
            className="text-700"
          >
            {r.label}
          </div>
        ))}
      </div>
    </div>
  );
};

const columns = [
  {
    accessorKey: 'jobIdShort',
    header: 'Job ID',
    cell: ({ row: { original } }) => (
      <Link
        to={`/apps/project-management/project-details/${original.jobId}`}
        className="text-decoration-none fw-bold fs-8"
        title={original.jobId}
      >
        {original.jobId.slice(0, 8).toUpperCase()}...
      </Link>
    ),
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'name',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <span className="fw-bold fs-8 d-inline-flex align-items-center gap-1">
        <ProductColorDot row={original} />
        {original.productLabel}
      </span>
    ),
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '14%' } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'Length (m)',
    cell: ({ row: { original } }) => (
      <span className="ps-3 fs-9 text-900">
        {Number(original.plannedLength || 0).toLocaleString()}
      </span>
    )
  },
  {
    id: 'estimate',
    header: 'Est. Left',
    enableSorting: false,
    cell: ({ row: { original } }) => <JobEstimate jobId={original.jobId} />,
    meta: {
      cellProps: { className: 'py-4' },
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    id: 'progressPhases',
    header: 'Phases',
    cell: ({ row: { original } }) => <PhaseProgressBar job={original} />,
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { width: '100%' } }
    }
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: ({ status }) => status?.label || status,
    cell: ({ row: { original } }) => {
      const usingStatus = getJobStatusUI(original.status);
      return (
        <Badge variant="phoenix" bg={usingStatus.type}>
          {usingStatus.label.toUpperCase()}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  }
];

const TopSection = ({ count }) => {
  const { setGlobalFilter } = useAdvanceTableContext();
  return (
    <div className="d-flex flex-wrap mb-3 gap-3 align-items-center justify-content-between">
      <SearchBox
        placeholder="Search jobs..."
        onChange={e => setGlobalFilter(e.target.value || undefined)}
        style={{ maxWidth: '24rem' }}
      />
      <span className="text-muted fs-9">{count} job(s)</span>
    </div>
  );
};

const SalesJobsTable = ({ jobs = [], isLoading }) => {
  const memoJobs = useMemo(() => jobs, [jobs]);

  const table = useAdvanceTable({
    data: memoJobs,
    columns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading jobs...</span>
      </div>
    );
  }

  if (!jobs.length) {
    return <div className="text-muted text-center py-4">No jobs found.</div>;
  }

  return (
    <JobEstimateProvider jobIds={memoJobs.map(j => j.jobId)}>
      <AdvanceTableProvider {...table}>
        <TopSection count={jobs.length} />
        <div className="border-bottom">
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table border-top border-200 fs-9'
            }}
          />
          <AdvanceTableFooter pagination className="py-3" />
        </div>
      </AdvanceTableProvider>
    </JobEstimateProvider>
  );
};

export default SalesJobsTable;
